import { Button, Modal, message } from 'antd'
import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { createUseStyles } from 'react-jss'
import { useAsync } from '../../hooks'
import { useIsAdmin } from '../../redux/slices/session'
import { toPath } from '../../routes'
import { signReports } from '../../api/reports'
import { ListError, PageHeader } from '../Common'
import CyclesFilter from '../Common/CyclesFilter'
import usePracticeCycles from '../Common/usePracticeCycles'
import useFilters from '../Cycles/useFilters'
import { useIsAcknowledged, useFetchReports } from '../../redux/slices/reports'
import Filters, { FIELDS_OPTIONS } from '../Forms/Filters'
import api from '../../api'
import Reports from './Reports'
import BatchDownloadButton from './BatchDownloadButton'
import BatchSignProgress from './ReportsBatchSign'

const DEFAULT_FILTERS = {
  signed: FIELDS_OPTIONS.signed[0].id,
  query: '',
}

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    justifyContent: 'flex-start',
  },
})

export default function PracticeReports() {
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [isModalVisible, setIsModalVisible] = useState(false)
  const isAdmin = useIsAdmin()
  const classes = useStyles()
  const [
    { error, clearError },
    { practice, pending, practiceId },
  ] = usePracticeCycles()
  const [officeLocations, setOfficeLocations] = useState([])
  const {
    conditions,
    setConditions,
    handleFilterChange,
    handlePageChange,
  } = useFilters({ ...DEFAULT_FILTERS, practiceId })
  const isAcknowledged = useIsAcknowledged(conditions.cycleId)
  const reportFilters = useMemo(() => {
    return {
      q: conditions.query,
      signed: conditions.signed,
      medicalProviderId: conditions.medicalProviderId,
      deviceTypeId: conditions.deviceTypeId,
      officeLocationId: conditions.officeLocationId,
    }
  }, [conditions])
  const { cycleId, page, perPage } = conditions

  const { pending: reportPending, execute: fetchReports } = useAsync(
    useFetchReports(practiceId, cycleId, page, reportFilters, perPage),
    false,
  )

  const [signProgress, setSignProgress] = useState(null)

  const sign = async () => {
    try {
      const res = await signReports(practiceId, selectedRowKeys)
      setIsModalVisible(false)

      const { length } = res.reports
      message.success(`${length} report${length !== 1 ? 's' : ''} signed.`)

      setSelectedRowKeys([])
      setSignProgress(res.batchId)
    } catch (e) {
      message.error('Failed to sign reports, please try again.')
    }
  }

  const handleSigningComplete = () => {
    setSignProgress(null)
    message.success(`Reports signed and regenerated successfully.`)
    setConditions({ ...conditions, page: 1 })
    fetchReports(practiceId, cycleId, 1, reportFilters)
  }

  useEffect(() => {
    if (practiceId) {
      api.practices.getPracticeOfficeLocations(practiceId).then(data => {
        setOfficeLocations(data)
      })
    }
  }, [practiceId])

  if (cycleId && !pending) {
    return (
      <div>
        <PageHeader
          title="Remote Reports"
          subTitle={isAdmin && practice.name}
          extra={[
            selectedRowKeys.length > 0 && (
              <Button key="sign" onClick={() => setIsModalVisible(true)}>
                {`Sign ${selectedRowKeys.length} report${
                  selectedRowKeys.length !== 1 ? 's' : ''
                }`}
              </Button>
            ),
            isAdmin && (
              <Link
                key="upload"
                to={toPath.cycles.cycle.reports.upload({ id: cycleId })}
              >
                <Button>Upload reports</Button>
              </Link>
            ),
            (isAcknowledged || isAdmin) && (
              <BatchDownloadButton key="download" cycleId={cycleId} />
            ),
          ]}
        />
        <div className={classes.container}>
          <CyclesFilter
            practiceId={practiceId}
            onSubmit={handleFilterChange}
            currentCycleId={cycleId}
          />
          <Filters
            defaultValues={DEFAULT_FILTERS}
            currentValues={conditions}
            placeholders={{ query: 'Search by name or device serial number' }}
            onSubmit={({
              query,
              signed,
              medicalProviderId,
              deviceTypeId,
              officeLocationId,
            }) =>
              setConditions({
                cycleId,
                query,
                signed,
                medicalProviderId,
                deviceTypeId,
                officeLocationId,
                page: 1,
              })
            }
            showDeviceType
            showMedicalProvider
            showSigningStatus
            showSearch
            showOfficeLocation={practiceId && officeLocations.length > 0}
          />
        </div>
        <Modal
          title="Sign reports"
          visible={isModalVisible}
          onOk={sign}
          onCancel={() => setIsModalVisible(false)}
          okText="Sign"
          cancelText="Cancel"
        >
          <p>{`You are about to sign ${
            selectedRowKeys.length !== 1
              ? `a batch of ${selectedRowKeys.length} reports`
              : '1 report'
          }.`}</p>
        </Modal>
        {signProgress && (
          <BatchSignProgress
            batchId={signProgress}
            onComplete={handleSigningComplete}
          />
        )}
        <Reports
          practiceId={practiceId}
          cycleId={cycleId}
          page={page}
          filters={reportFilters}
          onPageChange={handlePageChange}
          selected={selectedRowKeys}
          onSelect={setSelectedRowKeys}
          fetchReports={fetchReports}
          pendingFetch={reportPending}
        />
      </div>
    )
  }

  return (
    <div>
      {error && <ListError description={error.message} onClose={clearError} />}
    </div>
  )
}
